/**
 * Maps a thread's AGENT_ACTION entries to cache-invalidation changes.
 *
 * The thread agent writes entities on the thread branch at the SAME
 * `thread/<id>` revision the open pages are already reading, so a revision-keyed
 * fetch cannot see the write and the page stays stale until a manual refresh.
 * Every mutating agent tool already records the entity it touched (see
 * `action_tools.py` → `ActionRecord` → the persisted `action_detail`), so the
 * entries themselves carry exactly what the invalidation bus needs. No extra
 * wire format, and the entries are sequence-numbered, which is what makes
 * replay after a reconnect safe.
 */
import type { types } from "@/lib/openapi";
import type { EntityChange } from "@/stores/cache-invalidation";

type ThreadEntry = types.ThreadEntrySchema;

/**
 * Action types that rewrite a named attribute rather than the entity itself.
 *
 * Two caches narrow on an attribute instead of an entity type — the process
 * element list and the lane list, both of which the backend re-derives from the
 * BPMN diagram — so a definition rewrite has to be distinguishable from a plain
 * description edit or those lists would refetch on every keystroke burst.
 * Kept here rather than on the backend `ActionDetail`: the action type already
 * names the write precisely, so a field would be redundant on the wire.
 */
const ACTION_ATTRIBUTE: Readonly<Record<string, string>> = {
    set_process_definition: "process_definition",
    generate_bpmn_draft: "process_definition",
};

/**
 * The entity changes an agent's action entries represent, deduped.
 *
 * Failed actions are excluded: the tool reported an error and wrote nothing, so
 * refetching would only cost a request. Entries without an identifiable entity
 * (a tool that could not resolve the id it acted on) are excluded for the same
 * reason — there is nothing to key an invalidation on.
 */
export function entityChangesFromEntries(
    entries: readonly ThreadEntry[]
): EntityChange[] {
    const byKey = new Map<string, EntityChange>();

    for (const entry of entries) {
        if (entry.type !== "agent_action") continue;
        const detail = entry.action_detail;
        if (!detail || detail.success === false) continue;
        if (!detail.entity_type || !detail.entity_id) continue;

        const attribute = ACTION_ATTRIBUTE[detail.action_type];
        const key = `${detail.entity_type}|${detail.entity_id}|${attribute ?? ""}`;
        if (byKey.has(key)) continue;

        byKey.set(key, {
            entityType: detail.entity_type,
            entityId: detail.entity_id,
            ...(attribute ? { attribute } : {}),
            origin: "thread-agent",
        });
    }

    return [...byKey.values()];
}
